/**
 * SEP 5.e — Backend indexer confirmation controller.
 *
 * A SEPARATE authority from `PurchaseController`. The on-chain
 * controller stops at `confirmed_onchain` (a terminal state). This
 * module resumes from that terminal state and drives the second
 * confirmation flow: backend indexer polling of `/api/v1/purchases`.
 *
 * Invariants (mirror Neo's §A-§AA):
 *   - On-chain controller stays FROZEN. This module never writes,
 *     never signs, never touches the wallet, never calls SaleV2.
 *   - Backend/indexer is UNIQUE authority for indexed_successfully,
 *     parcel_id, canonical record. NEVER synthesize a PurchasePublic.
 *   - Poll only when: purchase.state.kind === "confirmed_onchain"
 *     AND txHash present AND snapshot present.
 *   - No auto-migration on account/chain/selection drift: the poller
 *     targets the original snapshot+txHash immutably.
 *   - Race-guarded: a single active poll loop per active generation.
 *     Stale HTTP responses cannot overwrite fresh state.
 *   - Bounded pagination: at most INDEXING_MAX_PAGES_PER_ATTEMPT pages
 *     per attempt (cursor from the backend, never manual).
 *   - Server-side filter: buyer + generation=v2 + canonical=true. No
 *     tx_hash server filter (backend does NOT expose one — see
 *     backend/app/routes/purchases.py §Filters). tx_hash match is
 *     done client-side after case-insensitive normalization.
 */
import type { GridSelection } from "./grid-selection";
import type { PurchasePublic, PurchasesPage } from "./sale-types";
import type { PurchaseSnapshot } from "./salev2-purchase";

// ---------------------------------------------------------------------
// Policy constants (Neo §J, §W)
// ---------------------------------------------------------------------
/** Poll interval between attempts (ms). Minimum enforced 3000. */
export const INDEXING_POLL_INTERVAL_MS = 3000;
/** Hard cap on attempts (~60 s at 3 s each). */
export const INDEXING_MAX_ATTEMPTS = 20;
/** Bounded pagination inside a single attempt. Newest-first ordering
 *  + buyer server-side filter make page 1 sufficient in practice; we
 *  keep a small ceiling for the tail-case where multiple purchases
 *  from the same buyer happen in the same window. */
export const INDEXING_MAX_PAGES_PER_ATTEMPT = 3;

// ---------------------------------------------------------------------
// State machine (Neo §H, §I)
// ---------------------------------------------------------------------
export interface IndexingCtx {
  readonly txHash: `0x${string}`;
  readonly snapshot: PurchaseSnapshot;
}

export type IndexingState =
  | { kind: "idle" }
  | {
      kind: "indexing_pending";
      txHash: `0x${string}`;
      snapshot: PurchaseSnapshot;
      attempt: number;
      lastError?: string;
    }
  | {
      kind: "indexed_successfully";
      txHash: `0x${string}`;
      snapshot: PurchaseSnapshot;
      record: PurchasePublic;
    }
  | {
      kind: "indexing_delayed";
      txHash: `0x${string}`;
      snapshot: PurchaseSnapshot;
      attempts: number;
      lastError?: string;
    }
  | {
      kind: "indexing_anomaly";
      txHash: `0x${string}`;
      snapshot: PurchaseSnapshot;
      record: PurchasePublic;
      reason: string;
    };

export type IndexingStateKind = IndexingState["kind"];

// ---------------------------------------------------------------------
// Match logic (Neo §E, §F, §O)
// ---------------------------------------------------------------------
export type MatchOutcome =
  | { kind: "no_match" }
  | { kind: "success"; record: PurchasePublic }
  | { kind: "anomaly"; record: PurchasePublic; reason: string };

const eqAddr = (a: string, b: string): boolean =>
  a.toLowerCase() === b.toLowerCase();

/**
 * Neo §E — match authority:
 *   - Primary key: tx_hash normalized case-insensitive.
 *   - indexed_successfully requires:
 *       tx_hash == confirmed txHash
 *       generation == "v2"
 *       canonical == true
 *       sale_contract_address == snapshot.saleAddress
 *       buyer == snapshot.owner
 *       (x, y, w, h) == snapshot.selection.(x, y, w, h)
 *   - Neo §F: same tx_hash but ANY identity field mismatches → anomaly.
 *   - Neo §O: canonical=false is NEVER success. It is NOT an anomaly
 *     either (backend reconciliation may still promote it). We treat
 *     it as "no_match" so the poller keeps polling within budget.
 */
export function matchPurchase(
  rec: PurchasePublic,
  ctx: IndexingCtx,
): MatchOutcome {
  if (rec.tx_hash.toLowerCase() !== ctx.txHash.toLowerCase()) {
    return { kind: "no_match" };
  }
  // Same tx_hash from here → the record IS about our on-chain tx.
  // Canonical=false: backend hasn't reconciled this event yet.
  // Not an anomaly — just keep polling.
  if (!rec.canonical) return { kind: "no_match" };
  // From this point, any identity drift IS an anomaly (Neo §F).
  if (rec.generation !== "v2") {
    return { kind: "anomaly", record: rec, reason: "generation_mismatch" };
  }
  if (!eqAddr(rec.sale_contract_address, ctx.snapshot.saleAddress)) {
    return { kind: "anomaly", record: rec, reason: "sale_address_mismatch" };
  }
  if (!eqAddr(rec.buyer, ctx.snapshot.owner)) {
    return { kind: "anomaly", record: rec, reason: "buyer_mismatch" };
  }
  const sel: GridSelection = ctx.snapshot.selection;
  if (rec.x !== sel.x || rec.y !== sel.y || rec.w !== sel.w || rec.h !== sel.h) {
    return { kind: "anomaly", record: rec, reason: "geometry_mismatch" };
  }
  return { kind: "success", record: rec };
}

// ---------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------
export interface IndexingFetchParams {
  buyer: `0x${string}`;
  generation: "v2";
  canonical: true;
  cursor?: string;
  page_size?: number;
}

export interface IndexingDeps {
  fetchPurchases: (params: IndexingFetchParams) => Promise<PurchasesPage>;
  /** Injected sleep — tests substitute with `async () => {}`. */
  sleep?: (ms: number) => Promise<void>;
}

type Listener = () => void;

interface AttemptOutcome {
  kind: "success" | "anomaly" | "no_match" | "transient_error";
  record?: PurchasePublic;
  anomalyReason?: string;
  error?: string;
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((res) => setTimeout(res, ms));

export class PurchaseIndexingController {
  private _state: IndexingState = { kind: "idle" };
  private readonly _listeners: Set<Listener> = new Set();
  /** Race guard — bumped on start/checkAgain/reset. Any async work
   *  captures the value at its start and bails out if it drifts. */
  private _gen = 0;
  private readonly _deps: IndexingDeps;

  constructor(deps: IndexingDeps) {
    this._deps = deps;
  }

  get state(): IndexingState {
    return this._state;
  }

  /** Test-only introspection: current active generation counter. */
  get currentGen(): number {
    return this._gen;
  }

  subscribe(l: Listener): () => void {
    this._listeners.add(l);
    return () => this._listeners.delete(l);
  }

  private setState(s: IndexingState): void {
    this._state = s;
    this._listeners.forEach((l) => l());
  }

  /**
   * Neo §I — start polling from confirmed_onchain. Idempotent: repeat
   * starts on the SAME (txHash, snapshot) while already pending are a
   * no-op; a new (txHash, snapshot) after a reset() proceeds.
   */
  start(txHash: `0x${string}`, snapshot: PurchaseSnapshot): void {
    if (this._state.kind !== "idle") return;
    this._gen += 1;
    const myGen = this._gen;
    this.setState({
      kind: "indexing_pending",
      txHash,
      snapshot,
      attempt: 0,
    });
    // Fire-and-forget the loop. It never rejects (all errors are
    // routed to state transitions).
    void this._loop(myGen, txHash, snapshot);
  }

  /**
   * Neo §L — user-initiated retry from indexing_delayed only.
   * Reuses the SAME txHash+snapshot; NO new blockchain write; a fresh
   * budget of INDEXING_MAX_ATTEMPTS.
   */
  checkAgain(): void {
    if (this._state.kind !== "indexing_delayed") return;
    const { txHash, snapshot } = this._state;
    this._gen += 1;
    const myGen = this._gen;
    this.setState({
      kind: "indexing_pending",
      txHash,
      snapshot,
      attempt: 0,
    });
    void this._loop(myGen, txHash, snapshot);
  }

  /** Neo §N/§U — hard stop. Any in-flight loop bails via gen guard. */
  reset(): void {
    this._gen += 1;
    this.setState({ kind: "idle" });
  }

  private async _loop(
    myGen: number,
    txHash: `0x${string}`,
    snapshot: PurchaseSnapshot,
  ): Promise<void> {
    const ctx: IndexingCtx = { txHash, snapshot };
    let lastError: string | undefined;
    for (let attempt = 1; attempt <= INDEXING_MAX_ATTEMPTS; attempt++) {
      if (myGen !== this._gen) return;
      // Update the attempt counter in state (kind stays pending).
      const cur = this._state;
      if (cur.kind === "indexing_pending") {
        this.setState({ ...cur, attempt });
      }

      const outcome = await this._attemptOnce(myGen, ctx);
      if (myGen !== this._gen) return;

      if (outcome.kind === "success" && outcome.record) {
        this.setState({
          kind: "indexed_successfully",
          txHash,
          snapshot,
          record: outcome.record,
        });
        return;
      }
      if (outcome.kind === "anomaly" && outcome.record) {
        this.setState({
          kind: "indexing_anomaly",
          txHash,
          snapshot,
          record: outcome.record,
          reason: outcome.anomalyReason ?? "identity_mismatch",
        });
        return;
      }
      if (outcome.kind === "transient_error") {
        lastError = outcome.error;
        // Neo §K — transient errors don't cancel; keep polling.
      }

      if (attempt >= INDEXING_MAX_ATTEMPTS) break;

      const sleep = this._deps.sleep ?? defaultSleep;
      await sleep(INDEXING_POLL_INTERVAL_MS);
      if (myGen !== this._gen) return;
    }

    if (myGen !== this._gen) return;
    // Neo §K — budget finished without confirmation → indexing_delayed
    // (NOT a failure, on-chain tx is confirmed).
    this.setState({
      kind: "indexing_delayed",
      txHash,
      snapshot,
      attempts: INDEXING_MAX_ATTEMPTS,
      lastError,
    });
  }

  private async _attemptOnce(
    myGen: number,
    ctx: IndexingCtx,
  ): Promise<AttemptOutcome> {
    let cursor: string | undefined;
    for (let page = 1; page <= INDEXING_MAX_PAGES_PER_ATTEMPT; page++) {
      if (myGen !== this._gen) return { kind: "no_match" };
      let resp: PurchasesPage;
      try {
        resp = await this._deps.fetchPurchases({
          buyer: ctx.snapshot.owner,
          generation: "v2",
          canonical: true,
          ...(cursor ? { cursor } : {}),
        });
      } catch (e) {
        return {
          kind: "transient_error",
          error: e instanceof Error ? e.message : String(e),
        };
      }
      if (myGen !== this._gen) return { kind: "no_match" };

      for (const rec of resp.purchases) {
        const m = matchPurchase(rec, ctx);
        if (m.kind === "success") return { kind: "success", record: m.record };
        if (m.kind === "anomaly") {
          return {
            kind: "anomaly",
            record: m.record,
            anomalyReason: m.reason,
          };
        }
      }
      if (!resp.next_cursor) return { kind: "no_match" };
      cursor = resp.next_cursor;
    }
    // Page bound reached without match.
    return { kind: "no_match" };
  }
}

// ---------------------------------------------------------------------
// React hook wrapper
// ---------------------------------------------------------------------
import { useCallback, useEffect, useRef, useState } from "react";
import { saleApi } from "./sale-api";

export interface UsePurchaseIndexingReturn {
  state: IndexingState;
  controller: PurchaseIndexingController;
  isPolling: boolean;
  checkAgain: () => void;
  reset: () => void;
}

export function usePurchaseIndexing(): UsePurchaseIndexingReturn {
  const controllerRef = useRef<PurchaseIndexingController | null>(null);
  if (controllerRef.current == null) {
    controllerRef.current = new PurchaseIndexingController({
      fetchPurchases: (params) => saleApi.purchases(params),
    });
  }
  const c = controllerRef.current;
  const [, force] = useState(0);
  useEffect(() => {
    const unsub = c.subscribe(() => force((x) => x + 1));
    return () => {
      unsub();
      // Neo §N/§U — component unmount stops polling.
      c.reset();
    };
  }, [c]);

  const checkAgain = useCallback(() => c.checkAgain(), [c]);
  const reset = useCallback(() => c.reset(), [c]);
  const isPolling = c.state.kind === "indexing_pending";
  return { state: c.state, controller: c, isPolling, checkAgain, reset };
}
